import { create } from "zustand";
import {
  fetchShopifyProducts,
  fetchShopifyCollections,
  fetchProductsByCollection,
  ShopifyProduct,
  ShopifyCollection,
} from "@/lib/shopify";
import { supabase } from "@/integrations/supabase/client";

interface SearchDefaults {
  default_collections: string[];
  show_categories: boolean;
}

interface GlobalPreloadStore {
  // Loading states
  isLoaded: boolean;
  isLoading: boolean;

  // Search settings
  searchSettings: SearchDefaults;

  // Products
  defaultProducts: ShopifyProduct[];
  allProducts: ShopifyProduct[];

  // Collections (all for navigation/search)
  allCollections: ShopifyCollection[];

  // Site settings cache
  siteSettings: Record<string, unknown>;

  // Actions
  preload: () => Promise<void>;
  refresh: () => Promise<void>;
  getSettingValue: <T>(key: string, fallback: T) => T;
}

export const useGlobalPreload = create<GlobalPreloadStore>((set, get) => ({
  isLoaded: false,
  isLoading: false,
  searchSettings: {
    default_collections: [],
    show_categories: true,
  },
  defaultProducts: [],
  allProducts: [],
  allCollections: [],
  siteSettings: {},

  preload: async () => {
    const { isLoaded, isLoading } = get();
    if (isLoaded || isLoading) return;

    set({ isLoading: true });

    try {
      // Fetch all in parallel for maximum efficiency
      const [settingsResult, productsResult, collectionsResult] = await Promise.all([
        // 1. Load all site settings
        supabase.from("site_settings").select("setting_key, setting_value"),
        // 2. Load products
        fetchShopifyProducts(100),
        // 3. Load all collections
        fetchAllCollectionsPaginated(),
      ]);

      // Process site settings into a map
      const siteSettings: Record<string, unknown> = {};
      if (settingsResult.data) {
        settingsResult.data.forEach((row) => {
          siteSettings[row.setting_key] = row.setting_value;
        });
      }

      // Extract search defaults
      let searchSettings: SearchDefaults = {
        default_collections: [],
        show_categories: true,
      };

      const searchDefaultsRaw = siteSettings["search_defaults"] as Record<string, unknown> | undefined;
      if (searchDefaultsRaw) {
        if (searchDefaultsRaw.default_collections && Array.isArray(searchDefaultsRaw.default_collections)) {
          searchSettings.default_collections = searchDefaultsRaw.default_collections as string[];
        } else if (searchDefaultsRaw.default_category && typeof searchDefaultsRaw.default_category === "string") {
          searchSettings.default_collections = [searchDefaultsRaw.default_category];
        }
        searchSettings.show_categories = (searchDefaultsRaw.show_categories as boolean) ?? true;
      }

      const allProducts = productsResult.edges.map((p) => p.node);

      // Load products from default collections if configured
      let defaultProducts: ShopifyProduct[] = [];
      if (searchSettings.default_collections.length > 0) {
        const collectionPromises = searchSettings.default_collections.map((handle) =>
          fetchProductsByCollection(handle, 12).catch(() => ({ edges: [] }))
        );
        const results = await Promise.all(collectionPromises);

        const productMap = new Map<string, ShopifyProduct>();
        results.forEach((result) => {
          result.edges.forEach((edge) => {
            if (!productMap.has(edge.node.id)) {
              productMap.set(edge.node.id, edge.node);
            }
          });
        });
        defaultProducts = Array.from(productMap.values()).slice(0, 24);
      }

      set({
        isLoaded: true,
        isLoading: false,
        siteSettings,
        searchSettings,
        defaultProducts: defaultProducts.length > 0 ? defaultProducts : allProducts.slice(0, 8),
        allProducts,
        allCollections: collectionsResult,
      });
    } catch (error) {
      console.error("Error in global preload:", error);
      set({ isLoading: false });
    }
  },

  refresh: async () => {
    set({ isLoaded: false, isLoading: false });
    await get().preload();
  },

  getSettingValue: <T>(key: string, fallback: T): T => {
    const { siteSettings } = get();
    return (siteSettings[key] as T) ?? fallback;
  },
}));

// Helper to fetch all collections with pagination
async function fetchAllCollectionsPaginated(): Promise<ShopifyCollection[]> {
  const allCollections: ShopifyCollection[] = [];
  let hasNextPage = true;
  let cursor: string | null = null;
  let pageCount = 0;
  const maxPages = 10; // Safety limit - 10 pages * 250 = 2500 collections max

  console.log('[Collections] Starting to fetch all collections with pagination...');

  while (hasNextPage && pageCount < maxPages) {
    pageCount++;
    console.log(`[Collections] Fetching page ${pageCount}, cursor: ${cursor ? cursor.substring(0, 20) + '...' : 'null'}`);
    
    try {
      const result = await fetchShopifyCollections(250, cursor);
      const fetchedCount = result.collections.length;
      allCollections.push(...result.collections);
      
      console.log(`[Collections] Page ${pageCount}: fetched ${fetchedCount}, total so far: ${allCollections.length}, hasNextPage: ${result.hasNextPage}`);
      
      hasNextPage = result.hasNextPage;
      cursor = result.endCursor;

      // If we got 0 results, stop to prevent infinite loop
      if (fetchedCount === 0) {
        console.log('[Collections] No more results, stopping pagination');
        break;
      }
    } catch (error) {
      console.error(`[Collections] Error fetching page ${pageCount}:`, error);
      break;
    }
  }

  console.log(`[Collections] Finished fetching. Total collections: ${allCollections.length}`);
  return allCollections;
}
